/*
 * sofsy-pdp.js: the Round 6 product page (storefront #49), phones and tablets.
 * Owns the behaviour of snippets/pdp-colour-size.liquid (P08 colour, P09 size), the size guide's chat link
 * (P22, snippets/size-chart.liquid) and the size-dependent parts of the P04 name block
 * (snippets/pdp-name-block.liquid: the price and the Complete the Set line).
 *
 * The one rule: today's controls stay the source of truth.
 * - The size is the checked input of <variant-radios> (sections/main-product.liquid). Each pill is a <label for>
 *   that input, so a tap is a tap on today's control and bundle.js / global.js do what they always did. This
 *   file only redraws the pills, the fit line and the name block FROM the checked input, after anything changed
 *   it: her tap, the bundle's KD-22 size memory after a colour swap, or the bundle choosing the first size in
 *   stock when the one in the link is sold out (it does that without an event, so we redraw on its
 *   'sizeChanged' and on 'change').
 * - The colour is today's link: bundle.js swaps the section when the tap lands on .color-swatch__item a.
 *
 * DS-S on a colour swap (P09: "a size she tapped stays when she switches shade if the new shade has it; a size
 * the rule chose is worked out again for the new shade"). bundle.js reloadSections() stores the checked size in
 * localStorage 'selectedSize', replaces the section, dispatches 'sectionReloadFinished', then re-checks the
 * stored size (reselectSizeAfterLoad). Our sectionReloadFinished listener runs in between: when the size was the
 * rule's, it clears the stored size, so the new shade keeps its own default (rendered checked by the server);
 * when the size was hers, it stores HER size, so it survives a shade where that size was sold out.
 * A size in the link (?variant=, e.g. from an ad) counts as hers.
 *
 * A sold-out pill opens the notify-me sheet for that variant (assets/notify-me.js, SofsyNotify.open).
 *
 * The product information tabs (P07, snippets/pdp-info-tabs.liquid): a tap or the arrow keys open a tab; one panel
 * shows at a time. The Reviews panel's list is mounted by bundle.js (initReviewsLazyLoad, an IntersectionObserver)
 * when the panel first comes into view, so it loads when the tab is opened, never before (a hidden panel is never
 * in view).
 *
 * The low-stock line (#63): "Only N left" in red for the chosen size (syncLowStock), from Liquid's counts; on
 * desktop, while a size is hovered, the hovered size's own line, as the fit line beside it does.
 *
 * Tracking: through window.sofsyTrack (assets/sofsy-track.js; the event dictionary's names). Events: low_stock_shown
 * {place: 'pdp', n, variant_id} once per variant per page view, size_selected
 * {size, variant_id, in_stock, place: 'pdp', changed_from_default} on every size tap, colour_swatch_clicked
 * {from_shade, to_shade, place: 'pdp'} when a circle opens another shade (both bus only in this release),
 * size_guide_help_tapped, info_tab_opened {tab}, privilege_section_viewed and delivery_block_viewed {variant:
 * grey_only | grey_only_intl} (each once per page view, at 50 % in view). Clarity's size_selected,
 * size_unavailable_clicked, size_guide_opened and colour_swatch_clicked still come from assets/clarity-tagging.js.
 *
 * The header (SR-1, self-review 2 Oct): html.sofsy-pdp-scrolled while the page is scrolled, so sofsy-pdp.css gives the
 * fixed, transparent header a white background once page content passes under it ("SR-1" below).
 *
 * Keyboard: the circles and the pills are radio groups with one tab stop each (the chosen one); the arrow keys,
 * Home and End move between them; Space (and Enter) chooses. Choosing a colour opens that shade. The tabs are a
 * tablist with one tab stop (the open tab); the arrow keys, Home and End move AND open (automatic activation).
 *
 * Never give this script the script-reload class: it binds once on document and survives every colour swap.
 */
(() => {
  'use strict';

  if (window.SofsyPdp) return;

  /* ---------- state that outlives a colour swap ---------- */
  let sizeSource = 'rule'; // 'rule': DS-S chose it; 'her': she chose it, or the link did
  let herSize = ''; // the Size value she chose
  let focusColourAfterSwap = false; // a keyboard opened another shade: put focus on its circle afterwards

  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const checkedRadio = () => document.querySelector('variant-radios input:checked');
  const frame = () => document.querySelector('[data-pdp-cs]');
  const pillLabels = () => all('label[data-pdp-size]');
  const pillControl = (label) => label.querySelector('[role="radio"]');
  const colourLinks = () => all('a[data-pdp-colour]');

  const track = (name, props) => {
    try {
      if (typeof window.sofsyTrack === 'function') window.sofsyTrack(name, props || {});
    } catch (_) {
      /* tracking never breaks the page */
    }
  };

  const parse = (text) => {
    try {
      return text ? JSON.parse(text) : null;
    } catch (_) {
      return null;
    }
  };

  /* ---------- redraw from the checked input ---------- */

  function syncSize() {
    const radio = checkedRadio();
    const labels = pillLabels();
    let chosen = null;

    labels.forEach((label) => {
      const on = !!radio && label.htmlFor === radio.id && !label.hasAttribute('data-sold');
      const control = pillControl(label);
      if (on) chosen = label;
      if (!control) return;
      control.classList.toggle('ds-pill--chosen', on);
      control.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    // one tab stop: the chosen pill, else the first
    labels.forEach((label, i) => {
      const control = pillControl(label);
      if (control) control.setAttribute('tabindex', (chosen ? label === chosen : i === 0) ? '0' : '-1');
    });

    // Add to Bag's words follow the chosen pill (sofsy-pdp.css: .pdp-size-chosen), never bundle.js's .size-selected,
    // which a product with a Colour option never gets. No pills on the page: the server's value stands.
    const form = document.getElementById('product_form_id');
    if (form && labels.length) form.classList.toggle('pdp-size-chosen', !!chosen);

    const fit = document.querySelector('[data-pdp-fit]');
    if (fit && !fit.hasAttribute('data-one-size')) {
      const size = chosen ? chosen.dataset.pdpSize : '';
      const words = chosen ? chosen.dataset.fit || '' : '';
      const sizeEl = fit.querySelector('[data-pdp-fit-size]');
      const dot = fit.querySelector('[data-pdp-fit-dot]');
      const wordsEl = fit.querySelector('[data-pdp-fit-words]');
      if (sizeEl && sizeEl.textContent !== size) sizeEl.textContent = size;
      if (wordsEl && wordsEl.textContent !== words) wordsEl.textContent = words;
      if (dot) dot.hidden = !(size && words);
    }

    syncNameBlock(radio);
    syncLowStock(radio);
    syncColourLinks();
  }

  // #63 low-stock line: "Only N left" for the chosen size when it has 1 to the threshold units (Liquid's counts in
  // script[data-pdp-low-stock], snippets/pdp-colour-size.liquid; empty when the setting is off). Phones: on the fit
  // line ([data-pdp-low]); desktop: at the left of View Size Guide ([data-pdp-low-desk]). low_stock_shown once per
  // variant per page view.
  const lowStockSeen = new Set();
  // The line for one variant: {id, n, words, show}. A variant not in the counts (more than the threshold, sold out,
  // not tracked) shows nothing.
  function lowStockFor(variantId) {
    const data = document.querySelector('script[data-pdp-low-stock]');
    const counts = data ? parse(data.textContent) : null;
    const id = String(variantId || '');
    const n = counts && id ? Number(counts[id]) || 0 : 0;
    const words = n > 0 && data ? String(data.dataset.words || '').replace('%n%', String(n)) : '';
    return { id, n, words, show: n > 0 && !!words };
  }
  function drawLowDesk({ words, show }) {
    all('[data-pdp-low-desk]').forEach((slot) => {
      if (slot.textContent !== words) slot.textContent = words;
      slot.hidden = !show;
    });
  }
  function syncLowStock(radio) {
    const { id, n, words, show } = lowStockFor(radio ? radio.dataset.variantId : '');

    all('[data-pdp-low]').forEach((slot) => {
      const text = slot.querySelector('[data-pdp-low-words]');
      if (text && text.textContent !== words) text.textContent = words;
      slot.hidden = !show;
    });
    drawLowDesk({ words, show });

    if (show && !lowStockSeen.has(id)) {
      lowStockSeen.add(id);
      track('low_stock_shown', { place: 'pdp', n, variant_id: Number(id) });
    }
  }

  // #63 A12: each circle's link carries her size in that shade (&variant=), so the shade bundle.js loads is
  // rendered with her size already chosen and no other pill flashes first. While the size is the rule's (DS-S),
  // no variant: the new shade works out its own default. The links' data-size-variants come from
  // snippets/pdp-colour-size.liquid.
  function syncColourLinks() {
    const size = sizeSource === 'her' ? herSize : '';
    all('a[data-pdp-colour][data-size-variants]').forEach((link) => {
      if (link.getAttribute('aria-checked') === 'true') return;
      const map = parse(link.dataset.sizeVariants) || {};
      let url;
      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch (_) {
        return;
      }
      const id = size && map[size] ? String(map[size]) : '';
      if ((url.searchParams.get('variant') || '') === id) return;
      if (id) url.searchParams.set('variant', id);
      else url.searchParams.delete('variant');
      const next = url.pathname + url.search;
      link.setAttribute('href', next);
      link.dataset.productUrl = next;
    });
  }

  // P04 name block: the price of her size, and Complete the Set only when enough pieces are in stock in it.
  function syncNameBlock(radio) {
    const block = document.querySelector('[data-pdp-name-block]');
    if (!block || !radio) return;

    const prices = parse(block.querySelector('script[data-pdp-prices]')?.textContent);
    const price = block.querySelector('[data-pdp-price]');
    const formatted = prices && prices[radio.dataset.variantId];
    if (price && formatted) {
      const text = String(formatted).replace(/ /g, ' ');
      if (price.textContent !== text) price.textContent = text;
    }

    const cts = block.querySelector('[data-cts-link="page"]');
    const counts = parse(block.dataset.ctsCounts);
    if (cts && counts) {
      const min = Number(block.dataset.ctsMin) || 2;
      // #63 (Ben, 3 Oct): and only while a set piece in stock in her size is not in the bag (any line of that
      // product, any size, any quantity, counts as in the bag). No stock list (an older render): the count alone.
      const stock = parse(block.dataset.ctsStock);
      const pieces = stock && Array.isArray(stock[radio.value]) ? stock[radio.value] : null;
      const inBag = bagHandles();
      const missing = pieces ? pieces.some((handle) => !inBag.has(handle)) : true;
      cts.hidden = !((Number(counts[radio.value]) || 0) >= min && missing);
    }
  }

  // The product handles in the bag, from the bag's own state (#bag-state, re-rendered by every cart write).
  function bagHandles() {
    const ci = document.querySelector('cart-items');
    let state = null;
    try {
      state = ci && typeof ci.readBagState === 'function' ? ci.readBagState() : parse(document.getElementById('bag-state')?.textContent);
    } catch (_) {
      state = null;
    }
    const lines = (state && Array.isArray(state.lines) && state.lines) || [];
    return new Set(lines.map((line) => line.product_handle).filter(Boolean));
  }

  let syncTimer = 0;
  // After the current event has run every handler (the bundle's included), never in the middle of them.
  const scheduleSync = () => {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncSize, 0);
  };

  /* ---------- size: taps ---------- */

  function openNotify(label) {
    const notify = window.SofsyNotify;
    if (!notify || typeof notify.open !== 'function') return;
    notify.open({
      variantId: label.dataset.variantId,
      size: label.dataset.pdpSize,
      shade: frame()?.dataset.shade || '',
      opener: pillControl(label) || label,
    });
  }

  document.addEventListener('click', (event) => {
    const label = event.target instanceof Element ? event.target.closest('label[data-pdp-size]') : null;
    if (!label) return;

    const tapped = label.dataset.pdpSize || '';
    const fromDefault = frame()?.dataset.defaultSize || '';
    // Every size tap (as Clarity's size_selected / size_unavailable_clicked, which count the same taps): in_stock
    // false is a sold-out size, which opens notify-me instead of choosing it.
    track('size_selected', {
      size: tapped,
      product_id: document.getElementById('main-product')?.getAttribute('product-id') || null,
      variant_id: label.dataset.variantId || null,
      in_stock: !label.hasAttribute('data-sold'),
      place: 'pdp',
      changed_from_default: !!(fromDefault && tapped && tapped !== fromDefault),
    });

    if (label.hasAttribute('data-sold')) {
      // Its input is disabled, so the label selects nothing: the tap asks to be told when it is back.
      event.preventDefault();
      openNotify(label);
      return;
    }

    sizeSource = 'her';
    herSize = tapped;
    scheduleSync(); // also when the size was already checked (no change event then)
  });

  /* ---------- colour: taps ---------- */

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[data-pdp-colour]') : null;
      if (!link) return;
      if (link.getAttribute('aria-checked') === 'true') {
        // Her shade already: nothing to load (bundle.js would reload the same section).
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      focusColourAfterSwap = event.detail === 0; // Enter, Space or a screen reader, not a finger
      // Bus only in this release: Clarity counts the same tap from clarity-tagging.js (.color-swatch__item).
      track('colour_swatch_clicked', {
        from_shade: frame()?.dataset.shade || '',
        to_shade: link.dataset.shadeName || '',
        place: 'pdp',
      });
    },
    true
  );

  /* ---------- keyboard: radio groups with one tab stop ---------- */

  function rove(event, items, current, choose) {
    const i = items.indexOf(current);
    if (i < 0) return;
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (i + 1) % items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (i - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
      case ' ':
      case 'Enter':
        if (event.key === 'Enter' && current.tagName === 'A') return; // a link opens itself on Enter
        event.preventDefault();
        choose();
        return;
      default:
        return;
    }
    event.preventDefault();
    items.forEach((item) => item.setAttribute('tabindex', item === items[next] ? '0' : '-1'));
    items[next].focus();
  }

  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || event.altKey || event.ctrlKey || event.metaKey) return;

    const link = target.closest('a[data-pdp-colour]');
    if (link) {
      rove(event, colourLinks(), link, () => link.click());
      return;
    }

    const control = target.closest('label[data-pdp-size] [role="radio"]');
    if (control) {
      rove(event, pillLabels().map(pillControl).filter(Boolean), control, () => control.closest('label').click());
    }
  });

  /* ---------- the bundle's events ---------- */

  document.addEventListener(
    'change',
    (event) => {
      if (event.target instanceof Element && event.target.matches('variant-radios input')) scheduleSync();
    },
    true
  );

  document.addEventListener('sizeChanged', scheduleSync);

  // Desktop size hover: bundle.js (sizeOnHover) shows the HOVERED size's fit line ("Fits 170 - 200 lb",
  // p[data-size-id]) while the pointer is on a variant-radios label[data-size] and the chosen size's again on mouseout
  // (initMiniSizeInfo). "Only N left" beside it follows the same size, so it never reads as the hovered size's
  // stock: the hovered size's own line (nothing when it is not low or is sold out), the chosen size's on pointerout.
  // The line only; low_stock_shown stays the chosen size's (syncLowStock).
  // Pointer events, not mouse events: a touch tap (an iPad wider than 1024 px) sends a compatibility mouseover with
  // no mouseout, so only a pointer that really hovers counts (pointerType mouse or pen; a touch is skipped, leaving
  // the chosen size's line as it was). Per event rather than matchMedia('(hover: hover)'), which reports only the
  // primary input: a touch laptop's mouse or an iPad's trackpad still hovers there.
  const sizeLabel = (event) =>
    event.pointerType !== 'touch' && event.target instanceof Element ? event.target.closest('variant-radios label[data-size]') : null;
  document.addEventListener('pointerover', (event) => {
    const label = sizeLabel(event);
    if (!label) return;
    const radio = (label.htmlFor && document.getElementById(label.htmlFor)) || checkedRadio();
    drawLowDesk(lowStockFor(radio ? radio.dataset.variantId : ''));
  });
  document.addEventListener('pointerout', (event) => {
    if (!sizeLabel(event)) return;
    const radio = checkedRadio();
    drawLowDesk(lowStockFor(radio ? radio.dataset.variantId : ''));
  });

  // #63: the bag changed (an add, a remove, a swap): Complete the Set under the price follows it.
  document.addEventListener('sofsy:bag-rendered', () => {
    const radio = checkedRadio();
    if (radio) syncNameBlock(radio);
  });

  // zao#616: is this Size value hidden until restock in the shade now on the page? Its input stays in the page with
  // data-hidden-size (sections/main-product.liquid; the rule is snippets/sofsy-size-hidden.liquid), so bundle.js
  // would still find it by value.
  const hiddenHere = (size) => !!size && all('variant-radios input[data-hidden-size]').some((input) => input.value === size);

  // Runs after the new section is in and BEFORE bundle.js re-checks the stored size (see the header).
  // zao#616: her size is not stored when the new shade hides it: bundle.js would check the hidden input, and the new
  // shade then keeps its own default, as for a shade that does not have her size. herSize itself is kept, so a later
  // shade that shows it gets it back.
  document.addEventListener('sectionReloadFinished', () => {
    const keep = sizeSource === 'her' && herSize && !hiddenHere(herSize) ? herSize : '';
    try {
      window.localStorage.setItem('selectedSize', keep);
    } catch (_) {
      /* storage blocked: the bundle then carries whatever it stored */
    }
    setTimeout(() => {
      syncSize();
      if (focusColourAfterSwap) {
        focusColourAfterSwap = false;
        const chosen = document.querySelector('a[data-pdp-colour][aria-checked="true"]');
        if (chosen) chosen.focus();
      }
    }, 0);
  });

  // bundle.js re-checks a size from the link on window load (reselectSizeAfterPageLoad), and picks the first size
  // in stock when that one is sold out; its load listener was added before this one, so this runs after it.
  window.addEventListener('load', () => setTimeout(syncSize, 0));

  /* ---------- P22: "Need sizing help? Chat with us" ---------- */

  function openChat() {
    const chat = window.GorgiasChat;
    try {
      if (chat && typeof chat.open === 'function') {
        if (!(typeof chat.isOpen === 'function' && chat.isOpen())) chat.open();
        return;
      }
    } catch (_) {
      /* fall through to the facade */
    }
    // Not loaded yet: the theme's chat facade loads it (when consent allows) and opens it.
    const facade = document.querySelector('.ChatFacade[data-facade="gorgias"]');
    if (facade) facade.click();
  }

  document.addEventListener('click', (event) => {
    const help = event.target instanceof Element ? event.target.closest('[data-size-guide-help]') : null;
    if (!help) return;
    event.preventDefault();
    track('size_guide_help_tapped');
    openChat();
  });

  /* ---------- P07: the product information tabs ---------- */

  const tabsOf = (root) => all('[data-pdp-tab]', root);

  function openTab(tab, { focus = false } = {}) {
    const root = tab.closest('[data-pdp-info]');
    if (!root) return;
    const wasOpen = tab.getAttribute('aria-selected') === 'true';
    tabsOf(root).forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
      const panel = document.getElementById(t.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
    if (!wasOpen) track('info_tab_opened', { tab: tab.dataset.pdpTabKind || '', layout: 'mobile' });
    watchViews(); // the privilege panel is inside Details: it can only be seen once Details is open again
  }

  document.addEventListener('click', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-pdp-tab]') : null;
    if (tab) openTab(tab);
  });

  // F3 (taxonomy v2.0 r6.1, spec 3.5): the desktop information popups (sections/main-product.liquid #tabsModals, one
  // <modal-opener> button per popup block) open a closed popup on every tap, so each tap is info_tab_opened
  // {tab, layout: 'desktop'}. tab uses the phone tabs' words, from the block's handleized title.
  const popupKind = (handle) => {
    const h = String(handle || '');
    if (/detail/.test(h)) return 'details';
    if (/deliver|return|shipping/.test(h)) return 'delivery';
    if (/review/.test(h)) return 'reviews';
    return 'other';
  };
  document.addEventListener('click', (event) => {
    const opener = event.target instanceof Element ? event.target.closest('#tabsModals .product-popup-modal__button[data-content-id]') : null;
    if (opener) track('info_tab_opened', { tab: popupKind(opener.getAttribute('data-sofsy-track-value')), layout: 'desktop' });
  });

  // F2 (spec 3.4): the legacy size sheet (snippets/quan-size-selector.liquid; today only when the quantity drawer did
  // not load) sends size_selected {place: 'size_menu'} on every size tap, sold-out included (its input is disabled,
  // so only the label hears that tap). One event per tap: the click the label passes on to its input is skipped.
  let menuLabelAt = -1e9;
  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    const label = t.closest('.size-selector-radios label[for]');
    let input = null;
    if (label) {
      input = document.getElementById(label.getAttribute('for'));
      menuLabelAt = Date.now();
    } else {
      input = t.closest('.size-selector-radios input[type="radio"]');
      if (!input || Date.now() - menuLabelAt < 400) return;
    }
    if (!input) return;
    const size = input.value || '';
    const fromDefault = frame()?.dataset.defaultSize || '';
    track('size_selected', {
      size,
      product_id: document.getElementById('main-product')?.getAttribute('product-id') || null,
      variant_id: input.dataset.variantId || null,
      in_stock: !input.disabled,
      place: 'size_menu',
      changed_from_default: !!(fromDefault && size && size !== fromDefault),
    });
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[data-pdp-tab]') : null;
    if (!tab || event.altKey || event.ctrlKey || event.metaKey) return;
    const tabs = tabsOf(tab.closest('[data-pdp-info]') || document);
    const i = tabs.indexOf(tab);
    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = (i + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (i - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return; // Enter and Space reach the <button> as a click
    }
    event.preventDefault();
    openTab(tabs[next], { focus: true });
  });

  /* ---------- seen once per page view, at 50 % in view: the returns line and the privilege panel ---------- */

  const seen = new Set();
  let viewObserver = null;

  function watchViews() {
    if (!('IntersectionObserver' in window)) return;
    if (!viewObserver) {
      viewObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            viewObserver.unobserve(el);
            if (el.matches('[data-pdp-ip]')) {
              if (seen.has('privilege')) return;
              seen.add('privilege');
              track('privilege_section_viewed');
            } else if (el.matches('[data-returns-line="pdp"]')) {
              if (seen.has('returns')) return;
              seen.add('returns');
              track('delivery_block_viewed', {
                variant: el.dataset.returnsRegion === 'us' ? 'grey_only' : 'grey_only_intl',
              });
            }
          });
        },
        { threshold: 0.5 }
      );
    }
    // A colour swap replaces both elements, so look them up again each time; each counts once per page view.
    if (!seen.has('privilege')) all('[data-pdp-ip]').forEach((el) => viewObserver.observe(el));
    if (!seen.has('returns')) all('[data-returns-line="pdp"]').forEach((el) => viewObserver.observe(el));
  }

  document.addEventListener('sectionReloadFinished', () => setTimeout(watchViews, 0));

  /* ---------- SR-1: the header turns solid white once the page scrolls under it (sofsy-pdp.css) ---------- */

  // bundle.js marks nothing on scroll, so this does: html.sofsy-pdp-scrolled while the page is scrolled past 0.
  // While the bag holds the page (assets/cart.js lockPage: body fixed at -y, so scrollY reads 0) the class is left
  // as it was; the bag's release scrolls her back, and that scroll sets it again. One check per frame, passive.
  // A13 (Ben, MC-02, 2 Oct): the white shows only while she scrolls. HEADER_IDLE_MS after the last scroll the class
  // goes and the white fades out with the same transition it fades with at the top (sofsy-pdp.css); the next scroll
  // brings it back. While the bag or the quantity drawer holds the page it stays, and fades once they let go.
  const HEADER_SCROLLED = 'sofsy-pdp-scrolled';
  const HEADER_IDLE_MS = 300;
  let headerFrame = 0;
  let headerIdle = 0;

  const headerHeld = () => {
    const list = document.documentElement.classList;
    return list.contains('sofsy-bag-lock') || list.contains('sofsy-qd-lock');
  };

  function syncHeader() {
    headerFrame = 0;
    const html = document.documentElement;
    if (html.classList.contains('sofsy-bag-lock')) return;
    html.classList.toggle(HEADER_SCROLLED, (window.scrollY || window.pageYOffset || 0) > 0);
    window.clearTimeout(headerIdle);
    if (html.classList.contains(HEADER_SCROLLED)) headerIdle = window.setTimeout(fadeHeader, HEADER_IDLE_MS);
  }

  function fadeHeader() {
    // held: look again a moment later, so the white fades once the bag or the drawer closes
    if (headerHeld()) {
      headerIdle = window.setTimeout(fadeHeader, HEADER_IDLE_MS);
      return;
    }
    headerIdle = 0;
    document.documentElement.classList.remove(HEADER_SCROLLED);
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!headerFrame) headerFrame = window.requestAnimationFrame(syncHeader);
    },
    { passive: true }
  );
  window.addEventListener('pageshow', syncHeader);

  /* ---------- start ---------- */

  // A size in the link that the server pre-checked is hers (it was chosen before she arrived).
  (() => {
    const fromLink = new URLSearchParams(window.location.search).get('variant');
    const radio = checkedRadio();
    if (fromLink && radio && radio.dataset.variantId === fromLink) {
      sizeSource = 'her';
      herSize = radio.value;
    }
  })();

  syncSize();
  watchViews();
  syncHeader();

  // Read-only, for the steps that follow (the quantity drawer opens "in the page's size").
  window.SofsyPdp = Object.freeze({
    state: () => {
      const radio = checkedRadio();
      return { variantId: radio ? radio.dataset.variantId : null, size: radio ? radio.value : null, source: sizeSource };
    },
    sync: syncSize,
  });
})();
